/**
 * Face Detection Extension for Scratch
 * =====================================
 * Implements all PictoBlox-compatible face detection blocks using
 * @vladmandic/face-api (TensorFlow.js wrapper).
 *
 * Features: face detection, landmarks, expressions, age/gender,
 *           face recognition via descriptor matching, bounding box overlay.
 *
 * Block opcodes, field names, menu names, and display text all match
 * PictoBlox's .sb3 format exactly so that pre-written PictoBlox
 * projects load and display seamlessly.
 */

import * as faceapi from '@vladmandic/face-api';
import formatMessage from 'format-message';

// ── PictoBlox expression ID mapping ─────────────────────────────
const EXPRESSION_BY_ID = {
    1: 'happy',
    2: 'sad',
    3: 'angry',
    4: 'fearful',
    5: 'disgusted',
    6: 'surprised',
    7: 'neutral'
};

// ── Landmark index helpers (68-point model) ─────────────────────
const LANDMARK_INDICES = {
    1: { name: 'leftEye', indices: [36, 37, 38, 39, 40, 41] },
    2: { name: 'rightEye', indices: [42, 43, 44, 45, 46, 47] },
    3: { name: 'nose', indices: [30] },
    4: { name: 'mouthLeft', indices: [48] },
    5: { name: 'mouthRight', indices: [54] },
    6: { name: 'jaw', indices: [8] }
};

// CDN for pre-trained model weights
const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model/';

class FaceDetectionExtension {
    constructor(runtime) {
        this.runtime = runtime;

        // ── Internal state ──────────────────────────────────────
        this._modelLoaded = false;
        this._modelLoading = false;
        this._faces = []; // full detection results
        this._drawBBox = false;
        this._threshold = 0.5;

        // Face recognition store: { name: [Float32Array] }
        this._savedDescriptors = {};
        this._matchedNames = []; // per-face matched name

        // Video / overlay
        this._video = null;
        this._videoReady = false;
        this._overlayCanvas = null;
        this._overlayCtx = null;

        if (this.runtime) {
            this.runtime.on('PROJECT_STOP_ALL', () => {
                this._stopVideo();
            });
        }
        
        // Trigger model loading immediately upon extension initialization
        this._loadModelsInit();
    }

    async _loadModelsInit() {
        if (this.runtime) {
            this.runtime.emit('MODEL_LOADING', 'Face Detection');
        }
        await this._loadModels();
        if (this.runtime) {
            this.runtime.emit('MODEL_LOADED', 'Face Detection');
        }
    }

    // ═══════════════════════════════════════════════════════════
    //  getInfo — block definitions
    //
    //  Block text matches PictoBlox screenshot EXACTLY.
    //  Opcodes, fields, inputs, and menu names match .sb3 format.
    // ═══════════════════════════════════════════════════════════

    _stopVideo() {
        if (this._video && this._video.srcObject) {
            const tracks = this._video.srcObject.getTracks();
            tracks.forEach(track => track.stop());
            this._video.srcObject = null;
        }
        this._videoReady = false;

        if (this._overlayCtx && this._overlayCanvas) {
            this._overlayCtx.clearRect(0, 0, this._overlayCanvas.width, this._overlayCanvas.height);
        }
    }

    getInfo() {
        return {
            id: 'faceDetection',
            name: formatMessage({
                id: 'gui.extension.faceDetection.name',
                default: 'Face Detection'
            }),
            color1: '#D65C5C',
            color2: '#B94A4A',
            blocks: [
                // ── Commands ────────────────────────────────────
                {
                    opcode: 'toggleStageVideoFeed',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'posenet.blocks.turnVideo',
                        default: 'turn [VIDEO_STATE] video on stage with [TRANSPARENCY] % transparency'
                    }),
                    arguments: {
                        VIDEO_STATE: {
                            type: 'string',
                            menu: 'videoStateMenu',
                            defaultValue: 'on'
                        },
                        TRANSPARENCY: {
                            type: 'number',
                            defaultValue: 0
                        }
                    }
                },
                {
                    opcode: 'drawBoundingBox',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'objectDetection.blocks.boundingBox',
                        default: '[OPTION] bounding box'
                    }),
                    arguments: {
                        OPTION: {
                            type: 'string',
                            menu: 'showHideMenu',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'setThreshold',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'objectDetection.blocks.setThreshold',
                        default: 'set detection threshold to [THRESHOLD]'
                    }),
                    arguments: {
                        THRESHOLD: {
                            type: 'string',
                            menu: 'threshold',
                            defaultValue: '0.5'
                        }
                    }
                },
                {
                    opcode: 'analyseImage',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'objectDetection.blocks.analyseImage',
                        default: 'analyse image from [FEED]'
                    }),
                    arguments: {
                        FEED: {
                            type: 'string',
                            menu: 'feedMenu',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'saveImage',
                    blockType: 'command',
                    text: formatMessage({ id: 'faceDetection.blocks.addClass', default: 'add class [FACE] as [NAME] from [FEED]' }),
                    arguments: {
                        FACE: {
                            type: 'string',
                            menu: 'faceNumber',
                            defaultValue: '1'
                        },
                        NAME: {
                            type: 'string',
                            defaultValue: 'Jarvis'
                        },
                        FEED: {
                            type: 'string',
                            menu: 'feedMenu',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'deleteImage',
                    blockType: 'command',
                    text: formatMessage({ id: 'faceDetection.blocks.resetClass', default: 'reset class' })
                },
                {
                    opcode: 'doFaceMatching',
                    blockType: 'command',
                    text: formatMessage({ id: 'faceDetection.blocks.doMatching', default: 'do face matching on [FEED]' }),
                    arguments: {
                        FEED: {
                            type: 'string',
                            menu: 'feedMenu',
                            defaultValue: '1'
                        }
                    }
                },

                // ── Reporters ───────────────────────────────────
                {
                    opcode: 'getNumberFaces',
                    blockType: 'reporter',
                    text: formatMessage({ id: 'faceDetection.blocks.getNumFaces', default: 'get # faces' })
                },
                {
                    opcode: 'getOption',
                    blockType: 'reporter',
                    text: formatMessage({ id: 'faceDetection.blocks.getExpression', default: 'get expression of face [FACE]' }),
                    arguments: {
                        FACE: {
                            type: 'string',
                            menu: 'faceNumber',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'getAge',
                    blockType: 'reporter',
                    text: formatMessage({ id: 'faceDetection.blocks.getAge', default: 'get age of face [FACE]' }),
                    arguments: {
                        FACE: {
                            type: 'string',
                            menu: 'faceNumber',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'getGender',
                    blockType: 'reporter',
                    text: formatMessage({ id: 'faceDetection.blocks.getGender', default: 'get gender of face [FACE]' }),
                    arguments: {
                        FACE: {
                            type: 'string',
                            menu: 'faceNumber',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'boxPosition',
                    blockType: 'reporter',
                    text: formatMessage({ id: 'faceDetection.blocks.getPosition', default: 'get [POSITION] of face [FACE]' }),
                    arguments: {
                        POSITION: {
                            type: 'string',
                            menu: 'positionMenu',
                            defaultValue: '1'
                        },
                        FACE: {
                            type: 'string',
                            menu: 'faceNumber',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'faceLandmarksF',
                    blockType: 'reporter',
                    text: formatMessage({ id: 'faceDetection.blocks.getLandmarkPosition', default: 'get [POSITION] of [LANDMARK] of face [FACE]' }),
                    arguments: {
                        POSITION: {
                            type: 'string',
                            menu: 'positionXYMenu',
                            defaultValue: '1'
                        },
                        LANDMARK: {
                            type: 'string',
                            menu: 'landmarkMenu',
                            defaultValue: '1'
                        },
                        FACE: {
                            type: 'string',
                            menu: 'faceNumber',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'landmarks',
                    blockType: 'reporter',
                    text: formatMessage({ id: 'faceDetection.blocks.getLandmarkPosition2', default: 'get [POSITION] of landmark [LANDMARK] of face [FACE]' }),
                    arguments: {
                        POSITION: {
                            type: 'string',
                            menu: 'positionXYMenu',
                            defaultValue: '1'
                        },
                        LANDMARK: {
                            type: 'number',
                            defaultValue: 1
                        },
                        FACE: {
                            type: 'string',
                            menu: 'faceNumber',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'getFace',
                    blockType: 'reporter',
                    text: formatMessage({ id: 'faceDetection.blocks.getClass', default: 'get class of face [FACE] detected' }),
                    arguments: {
                        FACE: {
                            type: 'string',
                            menu: 'faceNumber',
                            defaultValue: '1'
                        }
                    }
                },

                // ── Booleans ────────────────────────────────────
                {
                    opcode: 'isExpression',
                    blockType: 'Boolean',
                    text: formatMessage({ id: 'faceDetection.blocks.isExpression', default: 'is expression of face [FACE] [EXPRESSION]' }),
                    arguments: {
                        FACE: {
                            type: 'string',
                            menu: 'faceNumber',
                            defaultValue: '1'
                        },
                        EXPRESSION: {
                            type: 'string',
                            menu: 'expression',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'isClassDetected',
                    blockType: 'Boolean',
                    text: formatMessage({ id: 'faceDetection.blocks.isClassDetected', default: 'is [FACE] class detected' }),
                    arguments: {
                        FACE: {
                            type: 'string',
                            menu: 'faceNumber',
                            defaultValue: '1'
                        }
                    }
                }
            ],
            menus: {
                // ── Menus used as FIELDS (static dropdowns) ─────
                feedMenu: {
                    items: [
                        { text: 'camera', value: '1' },
                        { text: 'stage', value: '2' }
                    ]
                },
                videoStateMenu: {
                    items: ['on', 'off']
                },
                showHideMenu: {
                    items: [
                        { text: 'show', value: '1' },
                        { text: 'hide', value: '0' }
                    ]
                },
                positionMenu: {
                    items: [
                        { text: 'x position', value: '1' },
                        { text: 'y position', value: '2' },
                        { text: 'width', value: '3' },
                        { text: 'height', value: '4' }
                    ]
                },
                positionXYMenu: {
                    items: [
                        { text: 'x position', value: '1' },
                        { text: 'y position', value: '2' }
                    ]
                },
                landmarkMenu: {
                    items: [
                        { text: 'left eye', value: '1' },
                        { text: 'right eye', value: '2' },
                        { text: 'nose', value: '3' },
                        { text: 'mouth left', value: '4' },
                        { text: 'mouth right', value: '5' },
                        { text: 'jaw', value: '6' }
                    ]
                },

                // ── Menus used as INPUTS (shadow menu blocks) ───
                // These generate faceDetection_menu_<name> opcodes.
                // Field name inside shadow MUST be lowercase and match
                // the menu key exactly for .sb3 compatibility.
                faceNumber: {
                    acceptReporters: true,
                    items: ['1', '2', '3', '4', '5']
                },
                threshold: {
                    acceptReporters: true,
                    items: ['0.1', '0.2', '0.3', '0.4', '0.5', '0.6', '0.7', '0.8', '0.9']
                },
                expression: {
                    acceptReporters: true,
                    items: [
                        { text: 'happy', value: '1' },
                        { text: 'sad', value: '2' },
                        { text: 'angry', value: '3' },
                        { text: 'fearful', value: '4' },
                        { text: 'disgusted', value: '5' },
                        { text: 'surprised', value: '6' },
                        { text: 'neutral', value: '7' }
                    ]
                }
            }
        };
    }

    // ═══════════════════════════════════════════════════════════
    //  Private helpers
    // ═══════════════════════════════════════════════════════════

    /** Load all face-api models from CDN (once). */
    async _loadModels() {
        if (this._modelLoaded || this._modelLoading) return;
        this._modelLoading = true;
        try {
            // Ensure the TensorFlow backend is initialized
            await faceapi.tf.setBackend('webgl');
            await faceapi.tf.ready();

            await Promise.all([
                faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
                faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
                faceapi.nets.faceExpressionNet.loadFromUri(MODEL_URL),
                faceapi.nets.ageGenderNet.loadFromUri(MODEL_URL),
                faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
            ]);
            this._modelLoaded = true;
            console.log('[FaceDetection] All models loaded successfully.');
        } catch (err) {
            console.error('[FaceDetection] Model loading failed:', err);
        }
        this._modelLoading = false;
    }

    /** Create or return the hidden <video> element for webcam capture. */
    async _ensureVideo() {
        if (this._video && this._videoReady) return this._video;
        if (!this._video) {
            this._video = document.createElement('video');
            this._video.setAttribute('autoplay', '');
            this._video.setAttribute('playsinline', '');
            this._video.width = 480;
            this._video.height = 360;
            this._video.style.display = 'none';
            document.body.appendChild(this._video);
        }
        if (!this._videoReady) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { width: 480, height: 360 }
                });
                this._video.srcObject = stream;
                await this._video.play();
                this._videoReady = true;
            } catch (err) {
                console.warn('[FaceDetection] Camera access denied:', err);
            }
        }
        return this._video;
    }

    /** Get or create the transparent overlay canvas on top of the stage. */
    _ensureOverlay() {
        if (this._overlayCanvas) return this._overlayCanvas;
        const stageCanvas =
            document.querySelector('[class*="stage_stage_"] canvas') ||
            document.querySelector('canvas');
        if (!stageCanvas) return null;
        const parent = stageCanvas.parentElement;
        this._overlayCanvas = document.createElement('canvas');
        this._overlayCanvas.width = stageCanvas.width;
        this._overlayCanvas.height = stageCanvas.height;
        this._overlayCanvas.style.cssText =
            'position:absolute;top:0;left:0;width:100%;height:100%;' +
            'pointer-events:none;z-index:100;';
        parent.style.position = 'relative';
        parent.appendChild(this._overlayCanvas);
        this._overlayCtx = this._overlayCanvas.getContext('2d');
        return this._overlayCanvas;
    }

    /** Clear the overlay canvas. */
    _clearOverlay() {
        if (this._overlayCtx && this._overlayCanvas) {
            this._overlayCtx.clearRect(
                0, 0, this._overlayCanvas.width, this._overlayCanvas.height
            );
        }
    }

    /** Draw bounding boxes + labels on the overlay. */
    _renderOverlay() {
        if (!this._drawBBox) {
            this._clearOverlay();
            return;
        }
        this._ensureOverlay();
        if (!this._overlayCtx) return;
        this._clearOverlay();

        const ctx = this._overlayCtx;
        const cW = this._overlayCanvas.width;
        const cH = this._overlayCanvas.height;
        const srcW = 480;
        const srcH = 360;
        const scaleX = cW / srcW;
        const scaleY = cH / srcH;

        this._faces.forEach((det, i) => {
            const box = det.detection.box;
            const x = box.x * scaleX;
            const y = box.y * scaleY;
            const w = box.width * scaleX;
            const h = box.height * scaleY;

            ctx.strokeStyle = '#00ff00';
            ctx.lineWidth = 2;
            ctx.strokeRect(x, y, w, h);

            const label = this._matchedNames[i] || `Face ${i + 1}`;
            const expr = this._getDominantExpression(i);
            const age = det.age ? Math.round(det.age) : '?';
            const gender = det.gender || '?';
            const text = `${label} | ${expr} | ${age} ${gender}`;

            ctx.font = '12px sans-serif';
            const tw = ctx.measureText(text).width;
            ctx.fillStyle = 'rgba(0,0,0,0.6)';
            ctx.fillRect(x, y - 18, tw + 8, 18);
            ctx.fillStyle = '#00ff00';
            ctx.fillText(text, x + 4, y - 4);
        });
    }

    /** Return the dominant expression name for face at given index. */
    _getDominantExpression(idx) {
        const det = this._faces[idx];
        if (!det || !det.expressions) return 'unknown';
        let best = 'neutral';
        let bestScore = 0;
        Object.entries(det.expressions).forEach(([expr, score]) => {
            if (score > bestScore) {
                bestScore = score;
                best = expr;
            }
        });
        return best;
    }

    /** Safe accessor for a face detection result by FACE argument. */
    _getFace(args) {
        const idx = (parseInt(args.FACE, 10) || 1) - 1;
        return this._faces[idx] || null;
    }

    // ═══════════════════════════════════════════════════════════
    //  Block implementations
    // ═══════════════════════════════════════════════════════════

    async toggleStageVideoFeed(args) {
        const state = args.VIDEO_STATE;
        const transparency = Math.max(0, Math.min(100, Number(args.TRANSPARENCY) || 0));
        try {
            const video = this.runtime.ioDevices.video;
            if (state === 'on') {
                video.enableVideo();
                await this._ensureVideo();
            } else {
                video.disableVideo();
            }
            video.setPreviewGhost(transparency);
        } catch (err) {
            if (state === 'on') await this._ensureVideo();
        }
    }

    async analyseImage() {
        await this._loadModels();
        const video = await this._ensureVideo();
        if (!video || !this._videoReady) {
            this._faces = [];
            return;
        }
        try {
            const options = new faceapi.SsdMobilenetv1Options({
                minConfidence: this._threshold
            });
            this._faces = await faceapi
                .detectAllFaces(video, options)
                .withFaceLandmarks()
                .withFaceExpressions()
                .withAgeAndGender()
                .withFaceDescriptors();
        } catch (err) {
            console.warn('[FaceDetection] Analysis error:', err);
            this._faces = [];
        }
        this._renderOverlay();
    }

    drawBoundingBox(args) {
        this._drawBBox = args.OPTION === '1';
        if (!this._drawBBox) this._clearOverlay();
        else this._renderOverlay();
    }

    setThreshold(args) {
        this._threshold = parseFloat(args.THRESHOLD) || 0.5;
    }

    saveImage(args) {
        const face = this._getFace(args);
        if (!face || !face.descriptor) return;
        const name = String(args.NAME || 'unknown').trim();
        if (!this._savedDescriptors[name]) {
            this._savedDescriptors[name] = [];
        }
        this._savedDescriptors[name].push(face.descriptor);
    }

    deleteImage() {
        this._savedDescriptors = {};
        this._matchedNames = [];
    }

    async doFaceMatching() {
        const names = Object.keys(this._savedDescriptors);
        if (names.length === 0 || this._faces.length === 0) {
            this._matchedNames = this._faces.map(() => 'unknown');
            return;
        }
        const labeledDescriptors = names.map(name =>
            new faceapi.LabeledFaceDescriptors(name, this._savedDescriptors[name])
        );
        const matcher = new faceapi.FaceMatcher(labeledDescriptors, this._threshold);
        this._matchedNames = this._faces.map(face => {
            if (!face.descriptor) return 'unknown';
            return matcher.findBestMatch(face.descriptor).label;
        });
        this._renderOverlay();
    }

    getNumberFaces() {
        return this._faces.length;
    }

    getOption(args) {
        const idx = (parseInt(args.FACE, 10) || 1) - 1;
        return this._getDominantExpression(idx);
    }

    getAge(args) {
        const face = this._getFace(args);
        if (!face || typeof face.age === 'undefined') return 0;
        return Math.round(face.age);
    }

    getGender(args) {
        const face = this._getFace(args);
        if (!face || !face.gender) return 'unknown';
        return face.gender;
    }

    boxPosition(args) {
        const face = this._getFace(args);
        if (!face) return 0;
        const box = face.detection.box;
        switch (args.POSITION) {
            case '1': return Math.round(box.x);
            case '2': return Math.round(box.y);
            case '3': return Math.round(box.width);
            case '4': return Math.round(box.height);
            default: return 0;
        }
    }

    faceLandmarksF(args) {
        const face = this._getFace(args);
        if (!face || !face.landmarks) return 0;
        const lm = LANDMARK_INDICES[args.LANDMARK];
        if (!lm) return 0;
        const points = face.landmarks.positions;
        let sum = 0;
        const indices = lm.indices;
        indices.forEach(i => {
            const pt = points[i];
            if (pt) sum += (args.POSITION === '1') ? pt.x : pt.y;
        });
        return Math.round(sum / indices.length);
    }

    landmarks(args) {
        const face = this._getFace(args);
        if (!face || !face.landmarks) return 0;
        const idx = (parseInt(args.LANDMARK, 10) || 1) - 1;
        const pt = face.landmarks.positions[idx];
        if (!pt) return 0;
        return Math.round((args.POSITION === '1') ? pt.x : pt.y);
    }

    getFace(args) {
        const idx = (parseInt(args.FACE, 10) || 1) - 1;
        return this._matchedNames[idx] || 'unknown';
    }

    isExpression(args) {
        const idx = (parseInt(args.FACE, 10) || 1) - 1;
        const dominant = this._getDominantExpression(idx);
        const target = EXPRESSION_BY_ID[args.EXPRESSION] || args.EXPRESSION;
        return dominant === target;
    }

    isClassDetected(args) {
        const idx = (parseInt(args.FACE, 10) || 1) - 1;
        return idx < this._faces.length;
    }
}

export default FaceDetectionExtension;
